import React from "react";
import { Bug, Code2, LayoutDashboard, PlugZap, ShieldCheck, Skull, Sparkles } from "lucide-react";
import { FOCUS_RING } from "./socUi";
import { AREA_ICON } from "./SocTile";
import { AREA_COLOR } from "./socChartTheme";
import { PINNED_PANEL_TOP } from "./PinnedNavigation";

// Each entry's icon: its area tile's icon in the area's colour, so the menu and the tiles match.
// Entries without a tile of their own have their own icon; Code & DevOps and Dark web (parts of
// External threat) take its colour, Vulnerabilities (part of Endpoint) the Endpoint colour, the Overview
// and Data connectors follow the text colour.
const NAV_ICON = {
  overview: LayoutDashboard,
  code: Code2,
  darkweb: Skull,
  connectors: PlugZap,
  vulnerabilities: Bug,
};
const NAV_COLOR = { code: AREA_COLOR.external, darkweb: AREA_COLOR.external, vulnerabilities: AREA_COLOR.endpoint };
const iconOf = (key) => NAV_ICON[key] || AREA_ICON[key] || ShieldCheck;
const colorOf = (key) => NAV_COLOR[key] || AREA_COLOR[key];

const NavIcon = ({ viewKey, testId }) => {
  const Icon = iconOf(viewKey);
  const color = colorOf(viewKey);
  return <Icon className="h-4 w-4 shrink-0" style={color ? { color } : undefined} aria-hidden="true" data-testid={testId} />;
};

const COUNT_CLASS = {
  hot: "bg-status-error text-white",
  warn: "bg-status-warning-bg text-status-warning",
  plain: "bg-theme-secondary text-content-secondary",
  ai: "bg-theme-secondary text-content-secondary",
};

// The featured entry (Agentic AI) is an ordinary entry with the AI area's colour on its icon and an "AI"
// tag: marked out, but not so loud that the page reads as an AI page.
const AI_ACCENT = "text-[color:var(--soc-v-fuchsia)]";

// Security Operations left menu (/soc). A sticky column on wide screens; below that the same entries
// scroll sideways in one row above the content. `groups` comes from buildNav (socNav.js).
const SocSideNav = ({ groups, active, onSelect }) => {
  const item = (v, compact) => {
    const on = v.key === active;
    return (
      <button
        key={v.key}
        type="button"
        aria-current={on ? "page" : undefined}
        onClick={() => onSelect(v.key)}
        data-testid={`soc-nav-${compact ? "m-" : ""}${v.key}`}
        data-featured={v.featured ? "true" : undefined}
        className={`flex shrink-0 items-center gap-2 rounded-md text-left text-sm transition-colors ${FOCUS_RING} ${
          compact ? "px-3 py-1.5 border" : "w-full min-h-[34px] border-l-[3px] pl-2 pr-2"
        } ${
          on
            ? `font-semibold text-accent-cyan bg-accent-cyan-muted ${compact ? "border-accent-cyan" : "border-l-accent-cyan border-y-transparent border-r-transparent"}`
            : `font-medium text-content-secondary hover:bg-theme-hover hover:text-content-primary ${compact ? "border-border" : "border-transparent"}`
        }`}
      >
        <NavIcon viewKey={v.key} testId={`soc-nav-icon-${compact ? "m-" : ""}${v.key}`} />
        <span className={compact ? "whitespace-nowrap" : "min-w-0 flex-1 truncate"}>{v.label}</span>
        {v.featured && (
          <span className={`rounded border border-current px-1 text-[10px] font-bold uppercase leading-4 tracking-wide ${AI_ACCENT}`}>AI</span>
        )}
        {v.count != null && (
          <span className={`rounded-full px-1.5 py-px text-[11px] font-semibold tabular-nums ${COUNT_CLASS[v.tone]}`}>
            {v.count}
          </span>
        )}
      </button>
    );
  };

  return (
    <>
      <nav aria-label="Unified Security Operations" className="hidden lg:block" data-testid="soc-side-nav">
        <div className={`sticky ${PINNED_PANEL_TOP} flex flex-col gap-0.5 rounded-xl border border-border bg-theme-card-solid p-2`}>
          {groups.map((g, i) => (
            <div key={g.label} className="flex flex-col gap-0.5">
              <p className={`flex items-center gap-1 px-2.5 pb-1 text-[11px] font-semibold uppercase tracking-wide text-content-muted ${
                i === 0 ? "pt-1" : "pt-3"}`}>
                {g.featured && <Sparkles className={`h-3 w-3 ${AI_ACCENT}`} aria-hidden="true" />}
                {g.label}
              </p>
              {g.views.map((v) => item(v, false))}
            </div>
          ))}
        </div>
      </nav>
      <nav aria-label="Unified Security Operations sections" className="mb-4 flex gap-1.5 overflow-x-auto pb-1 lg:hidden">
        {groups.flatMap((g) => g.views).map((v) => item(v, true))}
      </nav>
    </>
  );
};

export default SocSideNav;
